import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { usePagination } from '../../contexts/PaginationContext';
import { useState, useEffect, useRef } from 'react';
import {
  readPatientImageCacheEntry,
  getPatientImageWithCache,
} from '../../utils/patientImageCache';
import PaginationControls from '../../components/PaginationControls';
import { authFetch } from '../../utils/authFetch';
import PacientesFilters from './components/PacientesFilters';
import PacientesGrid from './components/PacientesGrid';
import { usePacientesFilters } from '../../hooks/usePacientesFilters';

const Pacientes = () => {
  const { userAPIData } = useUserAPI();  

  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Usar hook para gerenciar filtros com persistência
  const { filters, updateFilter, setFilters, resetFilters } = usePacientesFilters();
  const { searchInput, searchTerm, statusFilter, modalityFilter, shiftFilter } = filters;
  
  const searchTimeoutRef = useRef(null);
  const [modalidades, setModalidades] = useState([]);
  const [recentOnly, setRecentOnly] = useState(false);
  const [enabledOnly, setEnabledOnly] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Debounce para busca
  const handleSearchChange = (value) => {
    updateFilter('searchInput', value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      updateFilter('searchTerm', value);
    }, 500); // Aguarda 500ms após o usuário parar de digitar
  };

  const handleSearchSubmit = () => {
    updateFilter('searchTerm', searchInput);
  };

  const handleClearFilters = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    resetFilters();
    setRecentOnly(false);
    setPage(0);
  };

  const {
    page,
    setPage,
    pageSize,
    setTotalElements,
    setTotalPages,
  } = usePagination();

  const latestPageRef = useRef(page);

  useEffect(() => {
    latestPageRef.current = page;
  }, [page]);

  // Função para carregar imagens em paralelo
  const loadImagesInParallel = async (pacientesData, expectedPage) => {
    const imagePromises = pacientesData
      .filter((paciente) => !paciente.imageUrl)
      .map(async (paciente) => {
      try {
        const url = await getPatientImageWithCache(paciente.id);
        return {
          id: paciente.id,
          url: url || require('../../assets/img/perfilvazio.png'),
          loading: false
        };
      } catch (error) {
        return {
          id: paciente.id,
          url: require('../../assets/img/perfilvazio.png'),
          loading: false
        };
      }
    });

    const results = await Promise.all(imagePromises);
    
    setPacientes(prevPacientes => {
      if (latestPageRef.current !== expectedPage) return prevPacientes;

      return prevPacientes.map(paciente => {
        const result = results.find(r => r.id === paciente.id);
        return result ? { ...paciente, imageUrl: result.url, imageLoading: false } : paciente;
      });
    });
  };

  const isRecentPaciente = (paciente) => {
    if (!paciente.interventionStartDate) return false;
    const limite = new Date();
    limite.setHours(0, 0, 0, 0);
    limite.setDate(limite.getDate() - 30);
    const dataIntervencao = new Date(paciente.interventionStartDate);
    dataIntervencao.setHours(0, 0, 0, 0);
    return dataIntervencao >= limite;
  };


  useEffect(() => {
    const fetchModalidades = async () => {
      try {
        const response = await authFetch('modality');
        if (!response.ok) throw new Error('Erro ao carregar modalidades');
        const data = await response.json();
        setModalidades(data || []);
      } catch (err) {
        console.error('Erro ao carregar modalidades:', err);
      }
    };

    fetchModalidades();
  }, []);

  useEffect(() => {
    if (!location.search || modalidades.length === 0) return;

    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const modalityParam = params.get('modality');
    const filterParam = params.get('filter');
    const enabledOnlyParam = params.get('enabledOnly');

    if (statusParam || modalityParam || filterParam || enabledOnlyParam) {
      // Converter nome da modalidade para ID se necessário
      let modalityId = '';
      if (modalityParam) {
        const modality = modalidades.find(m => 
          m.name?.toUpperCase() === modalityParam.toUpperCase()
        );
        modalityId = modality ? modality.id.toString() : '';
      }

      setEnabledOnly(enabledOnlyParam === 'true');
      setFilters((prev) => ({
        ...prev,
        searchInput: '',
        searchTerm: '',
        statusFilter: statusParam ?? (filterParam === 'novos' ? '' : prev.statusFilter),
        modalityFilter: modalityId,
        shiftFilter: '',
      }));
      setRecentOnly(filterParam === 'novos');
      setPage(0);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate, setFilters, setPage, modalidades]);

  // Refs para rastrear valores anteriores e se é o mount inicial
  const prevSearchTermRef = useRef(searchTerm);
  const prevStatusFilterRef = useRef(statusFilter);
  const prevModalityFilterRef = useRef(modalityFilter);
  const prevShiftFilterRef = useRef(shiftFilter);
  const isInitialMountRef = useRef(true);

  // Inicializar refs no mount
  useEffect(() => {
    if (isInitialMountRef.current) {
      prevSearchTermRef.current = searchTerm;
      prevStatusFilterRef.current = statusFilter;
      prevModalityFilterRef.current = modalityFilter;
      prevShiftFilterRef.current = shiftFilter;
      isInitialMountRef.current = false;
    }
  }, []);

  // Resetar página apenas quando searchTerm realmente mudar (não no mount inicial)
  useEffect(() => {
    if (isInitialMountRef.current) return;
    
    if (prevSearchTermRef.current !== searchTerm) {
      prevSearchTermRef.current = searchTerm;
      setPage(0);
    }
  }, [searchTerm, setPage]);

  // Resetar página apenas quando filtros realmente mudarem (não no mount inicial)
  useEffect(() => {
    if (isInitialMountRef.current) return;
    
    if (
      prevStatusFilterRef.current !== statusFilter ||
      prevModalityFilterRef.current !== modalityFilter ||
      prevShiftFilterRef.current !== shiftFilter
    ) {
      prevStatusFilterRef.current = statusFilter;
      prevModalityFilterRef.current = modalityFilter;
      prevShiftFilterRef.current = shiftFilter;
      setPage(0);
      if (!statusFilter && !modalityFilter && !shiftFilter) {
        setRecentOnly(false);
      }
    }
  }, [statusFilter, modalityFilter, shiftFilter, setPage]);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;
    const currentPage = page;

    const fetchPacientes = async () => {
      setLoading(true);

      const baseUrl = `patient/${currentPage}/${pageSize}`;
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('name', searchTerm);
      if (statusFilter) queryParams.append('statusId', statusFilter);
      if (modalityFilter) queryParams.append('modalityId', modalityFilter);
      if (shiftFilter) queryParams.append('shift', shiftFilter);
      if (enabledOnly) queryParams.append('enabledOnly', 'true');
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

      try {
        const response = await authFetch(fullUrl, { signal: controller.signal });

        if (!response.ok) {
          throw new Error("Erro ao buscar Pacientes");
        }

        const data = await response.json();
        if (!isCurrent) return;

        const content = Array.isArray(data.content) ? data.content : [];
        let pacientesData = content.slice(0, pageSize).map((paciente) => {
          const cachedUrl = readPatientImageCacheEntry(paciente.id);
          return { 
            ...paciente, 
            imageUrl: cachedUrl,
            imageLoading: !cachedUrl,
          };
        });

        if (recentOnly) {
          pacientesData = pacientesData.filter(isRecentPaciente);
        }

        setPacientes(pacientesData);
        setError(null);
        setTotalElements(recentOnly ? pacientesData.length : Number(data.totalElements) || 0);
        setTotalPages(recentOnly ? 1 : Number(data.totalPages) || 0);

        loadImagesInParallel(pacientesData, currentPage);
      } catch (err) {
        if (err.name !== 'AbortError' && isCurrent) {
          setError(err.message);
        }
      } finally {
        if (isCurrent) {
        setLoading(false);
        }
      }
    };

    fetchPacientes();

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [
    page,
    pageSize,
    searchTerm,
    statusFilter,
    modalityFilter,
    shiftFilter,
    recentOnly,
    enabledOnly,
    setTotalElements,
    setTotalPages
  ]);

  if (error) return <p>Erro: {error}</p>;

  return (
    <div>
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Pacientes</h3>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Link
              to="/pacientes/novo"
              className="btn btn-info btn-sm"
            >
              Cadastrar Paciente
            </Link>

            {/* <Link
              to="/responsaveis"
              className="btn btn-info btn-sm"
            >
              Gerenciar Responsáveis
            </Link> */}

            {userAPIData.userRoleId === 1 && (
              <>
                <Link
                  to="/Planos"
                  className="btn btn-info btn-sm"
                >
                  Gerenciar Planos
                </Link>

                <Link
                  to="/salas"
                  className="btn btn-info btn-sm"
                >
                  Gerenciar Salas
                </Link>

                {/* <Link
                  to="/pacientes/exportardadospacientes"
                  className="btn btn-info btn-sm"
                >
                  Exportar dados para Excel
                </Link> */}
              </>
            )}
          </div>
        </div>

        <PacientesFilters
          searchInput={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          statusFilter={statusFilter}
          onStatusChange={(value) => updateFilter('statusFilter', value)}
          modalityFilter={modalityFilter}
          onModalityChange={(value) => updateFilter('modalityFilter', value)}
          shiftFilter={shiftFilter}
          onShiftChange={(value) => updateFilter('shiftFilter', value)}
          modalidades={modalidades}
          onClearFilters={handleClearFilters}
        />

        <PacientesGrid pacientes={pacientes} loading={loading} />
        <div className="row mt-4">
          <div className="col-12">
            <PaginationControls currentCount={pacientes.length} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Pacientes